import React, { Component } from "react";
import { formatDate, getViewPatientData } from "../../lib";
import Loader from "../Loader";
import lungsImage from "../../assets/lungs.png";
import kidneyImage from "../../assets/kidney.png";

const DISEASE_CONFIG = [
    { key: "Heart", label: "Heart Disease", icon: "❤️", color: "#e74c3c" },
    { key: "Cancer", label: "Cancer", icon: "🧬", color: "#8e44ad" },
    { key: "Chronic_Kidney_Disease", label: "Chronic Kidney Disease", icon: kidneyImage, color: "#2980b9", isImage: true },
    { key: "Lung", label: "Lung Disease", icon: lungsImage, color: "#27ae60", isImage: true },
    { key: "Dementia", label: "Dementia", icon: "🧠", color: "#e67e22" },
    { key: "Diabetes", label: "Diabetes", icon: "💉", color: "#16a085" },
    { key: "Down_Syndrome", label: "Down Syndrome", icon: "🎗️", color: "#2ecc71" },
    { key: "Immunocompromised", label: "Immunocompromised", icon: "🛡️", color: "#c0392b" },
    { key: "Liver_Disease", label: "Liver Disease", icon: "🟤", color: "#d35400" },
    { key: "Overweight_and_Obesity", label: "Overweight / Obesity", icon: "⚖️", color: "#7f8c8d" },
    { key: "Sickle_Cell_Disease_or_Thalassemia", label: "Sickle Cell / Thalassemia", icon: "🩸", color: "#c0392b" },
    { key: "Smoking_current_or_former", label: "Smoking History", icon: "🚬", color: "#636e72" },
    { key: "Solid_Organ_or_Blood_Stem_Cell_Transplant", label: "Organ / Stem Cell Transplant", icon: "🏥", color: "#0984e3" },
    { key: "Stroke_or_Cerebrovascular_Disease", label: "Stroke / Cerebrovascular", icon: "🧩", color: "#6c5ce7" },
    { key: "Substance_Use_Disorders", label: "Substance Use Disorders", icon: "⚠️", color: "#fdcb6e" },
    { key: "Mental_Health", label: "Mental Health", icon: "🌿", color: "#00b894" },
];

const DMI_CONFIG = {
    "Below average": { label: "Below Average", color: "#e74c3c", icon: "▼" },
    "Average": { label: "Average", color: "#f39c12", icon: "●" },
    "Above average": { label: "Above Average", color: "#27ae60", icon: "▲" },
};

/* ─────────────────────────────────────────────
   Sub-component: DiseaseCard
───────────────────────────────────────────── */
class DiseaseCard extends Component {
    constructor(props) {
        super(props);
        this.state = { hovered: false };
    }

    render() {
        const { label, icon, color, isActive, isImage } = this.props;
        const { hovered } = this.state;

        const cardStyle = {
            borderRadius: "12px",
            border: isActive ? `2px solid ${color}` : "2px solid #e0e0e0",
            backgroundColor: isActive
                ? hovered ? color : `${color}15`
                : hovered ? "#f9f9f9" : "#fff",
            transition: "all 0.25s ease",
            cursor: "default",
            padding: "18px 12px",
            boxShadow: isActive
                ? `0 4px 16px ${color}40`
                : "0 2px 6px rgba(0,0,0,0.06)",
            transform: hovered ? "translateY(-2px)" : "none",
            overflow: "hidden",
        };

        return (
            <div
                style={{ minWidth: 0 }}
                onMouseEnter={() => this.setState({ hovered: true })}
                onMouseLeave={() => this.setState({ hovered: false })}
            >
                <div className="text-center" style={cardStyle}>
                    <span style={{
                        fontSize: "28px", marginBottom: "6px", display: "block",
                        filter: isActive ? "none" : "grayscale(80%)",
                        opacity: isActive ? 1 : 0.45,
                    }}>
                        {isImage ? (
                            <img
                                src={icon}
                                alt={label}
                                style={{ width: "28px", height: "28px", objectFit: "contain" }}
                            />
                        ) : (
                            icon
                        )}
                    </span>
                    <div style={{
                        fontSize: "12px",
                        fontWeight: isActive ? "700" : "500",
                        color: isActive ? (hovered ? "#fff" : color) : "#aaa",
                        lineHeight: "1.3",
                        textAlign: "center",
                    }}>
                        {label}
                    </div>
                    <span style={{
                        display: "inline-block", marginTop: "8px",
                        padding: "2px 10px", borderRadius: "12px",
                        fontSize: "10px", fontWeight: "700", letterSpacing: "0.5px",
                        backgroundColor: isActive
                            ? (hovered ? "rgba(255,255,255,0.25)" : color)
                            : "#e0e0e0",
                        color: isActive ? "#fff" : "#aaa",
                    }}>
                        {isActive ? "DETECTED" : "NOT DETECTED"}
                    </span>
                </div>
            </div>
        );
    }
}

/* ─────────────────────────────────────────────
   Sub-component: StatBlock
───────────────────────────────────────────── */
class StatBlock extends Component {
    render() {
        const { label, value, subtitle, color, icon } = this.props;
        return (
            <div className="col-xs-6 col-md-3">
                <div style={{
                    backgroundColor: "#fff", borderRadius: "10px",
                    padding: "20px 16px", border: "1px solid #eee",
                    borderLeft: `4px solid ${color || "#3498db"}`,
                    marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}>
                    <div style={{ fontSize: "22px", marginBottom: "4px" }}>{icon}</div>
                    <div style={{ fontSize: "32px", fontWeight: "700", color: color || "#2c3e50" }}>
                        {value ?? "—"}
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#555" }}>{label}</div>
                    {subtitle && (
                        <div style={{ fontSize: "11px", color: "#aaa", marginTop: "2px" }}>{subtitle}</div>
                    )}
                </div>
            </div>
        );
    }
}

/* ─────────────────────────────────────────────
   Main component: ChronicConditions
───────────────────────────────────────────── */
class ChronicConditions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeFilter: "all",
            loading: true,
            error: null,
            data: null,         // null until API responds
        };
    }

    componentDidMount() {
        this.fetchDashboardData();
    }

    fetchDashboardData = async () => {
        try {
            this.setState({ loading: true, error: null });

            const apiUrl = `${this.props.settings.server.baseUrl}/fetch/chronic_conditions?memberid=${this.props?.mrn}`;

            const data = await getViewPatientData({
                url: apiUrl,
                server: this.props.settings.server,
            });

            console.log("api response", data);

            this.setState({
                data: data || null,
                loading: false,
            });
        } catch (error) {
            console.error("Dashboard fetch failed:", error);
            this.setState({ error: error.message, loading: false });
        }
    };

    /* ── Helpers ── */

    getRecord() {
        // Safely pull the first record from API response
        return this.state.data?.data?.[0] || {};
    }

    getActiveConditions() {
        const record = this.getRecord();
        return DISEASE_CONFIG.filter((d) => record[d.key] === 1);
    }

    getInactiveConditions() {
        const record = this.getRecord();
        return DISEASE_CONFIG.filter((d) => record[d.key] !== 1);
    }

    renderDMIBadge(dmi) {
        const config = DMI_CONFIG[dmi] || { label: dmi || "Unknown", color: "#aaa", icon: "●" };
        return (
            <span style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "6px 18px", borderRadius: "20px",
                backgroundColor: config.color, color: "#fff",
                fontWeight: "700", fontSize: "14px", letterSpacing: "0.3px",
            }}>
                <span>{config.icon}</span>
                {config.label}
            </span>
        );
    }

    renderFilteredDiseases() {
        const { activeFilter } = this.state;
        const record = this.getRecord();

        let list = DISEASE_CONFIG;
        if (activeFilter === "active") list = this.getActiveConditions();
        if (activeFilter === "inactive") list = this.getInactiveConditions();

        return list.map((d) => (
            <DiseaseCard
                key={d.key}
                label={d.label}
                icon={d.icon}
                isImage={d.isImage}
                color={d.color}
                isActive={record[d.key] === 1}
            />
        ));
    }

    /* ── Render ── */

    render() {
        const { loading, error, activeFilter, data } = this.state;

        // ── Loading state: show only spinner ──
        if (loading) {
            return (
                <div style={{ padding: "60px 0", textAlign: "center" }}>
                    <Loader />
                </div>
            );
        }

        // ── Error state ──
        if (error) {
            return (
                <div className="container-fluid" style={{ padding: "40px 20px" }}>
                    <div className="alert alert-danger" style={{ borderRadius: "10px" }}>
                        <strong>⚠️ Failed to load data.</strong> {error}
                        <button
                            className="btn btn-sm btn-default"
                            style={{ marginLeft: "16px" }}
                            onClick={this.fetchDashboardData}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        // ── No data returned ──
        if (!data || !data.data || data.data.length === 0) {
            return (
                <div className="container-fluid" style={{ padding: "40px 20px", textAlign: "center", color: "#aaa" }}>
                    <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
                    <p style={{ fontSize: "15px" }}>No chronic condition data available for this member.</p>
                </div>
            );
        }

        // ── Data ready: render full UI ──
        const record = this.getRecord();
        const activeConditions = this.getActiveConditions();
        const totalConditions = DISEASE_CONFIG.length;

        const sectionTitleStyle = {
            fontSize: "16px", fontWeight: "700", color: "#2c3e50",
            borderBottom: "2px solid #eef2f7", paddingBottom: "10px",
            marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px",
        };

        const panelStyle = {
            backgroundColor: "#fff", borderRadius: "12px",
            padding: "20px 24px", marginBottom: "22px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)", border: "1px solid #eef0f5",
        };

        const filterBtnBase = {
            borderRadius: "20px", fontSize: "12px", fontWeight: "600",
            padding: "5px 16px", marginRight: "8px",
            border: "1.5px solid #ddd", cursor: "pointer", transition: "all 0.2s",
            outline: "none",
        };

        const diseaseGridStyle = {
            display: "grid",
            gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
            gap: "16px",
        };

        const filteredDiseases = this.renderFilteredDiseases();

        return (
            <div style={{
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                backgroundColor: "#f4f7fb", minHeight: "100vh", padding: "20px 0",
            }}>
                <div className="container-fluid" style={{ margin: "0 auto", padding: "0 20px" }}>

                    {/* ── HEADER ── */}
                    <div style={{
                        background: "linear-gradient(135deg, rgb(196 196 219) 0%, rgb(61 78 124) 60%, rgb(166 194 227) 100%)",
                        borderRadius: "14px", padding: "15px 28px", marginBottom: "24px",
                        color: "#fff", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                    }}>
                        <div className="row">
                            <div className="col-sm-8">
                                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    <div style={{
                                        width: "56px", height: "56px", borderRadius: "50%",
                                        background: "rgba(255,255,255,0.15)", display: "flex",
                                        alignItems: "center", justifyContent: "center",
                                        fontSize: "26px", border: "2px solid rgba(255,255,255,0.3)",
                                    }}>
                                        🏥
                                    </div>
                                    <div>
                                        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#fff" }}>
                                            Health Disease Profile
                                        </h2>
                                        <p style={{ margin: 0, color: "rgba(255,255,255,0.6)", fontSize: "13px" }}>
                                            Member ID: {record.Health_Plan_Member_ID || "—"}&nbsp;|&nbsp;
                                            Report: {formatDate(record.Report_Date_Time)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-4 text-right">
                                <div style={{ marginTop: "8px" }}>
                                    <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", marginBottom: "6px" }}>
                                        Disease Management Index
                                    </div>
                                    {this.renderDMIBadge(record.Disease_Management_Index)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SUMMARY STATS ── */}
                    <div className="row">
                        <StatBlock icon="🦠" label="Active Conditions" value={record.Chronic_Condition_Count} subtitle={`of ${totalConditions} tracked`} color="#e74c3c" />
                        <StatBlock icon="🏨" label="Office Visits" value={record.Office_Visit_Count} subtitle="Total recorded" color="#3498db" />
                        <StatBlock icon="🚑" label="ER Visits" value={record.ER_Count} subtitle="Total recorded" color="#e67e22" />
                        <StatBlock icon="🛏️" label="Admissions" value={record.All_Cause_Admission_Count} subtitle="All-cause" color="#9b59b6" />
                    </div>

                    {/* ── RECENT ACTIVITY ── */}
                    <div className="row">
                        {/* ED */}
                        <div className="col-md-6">
                            <div style={panelStyle}>
                                <div style={sectionTitleStyle}>
                                    <span>🚑</span> Emergency Department Activity
                                </div>
                                <table className="table table-condensed" style={{ marginBottom: 0 }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>Last ED Visit</td>
                                            <td style={{ fontWeight: "600" }}>{formatDate(record.Date_of_most_recent_ED_visit)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>ED Visits (past 6 days)</td>
                                            <td>
                                                <span className={`label label-${record.Number_of_ED_visits_in_the_past_6_days > 0 ? "danger" : "default"}`}>
                                                    {record.Number_of_ED_visits_in_the_past_6_days ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>ED Visits (past 29 days)</td>
                                            <td>
                                                <span className={`label label-${record.Number_of_ED_visits_in_the_past_29_days > 0 ? "warning" : "default"}`}>
                                                    {record.Number_of_ED_visits_in_the_past_29_days ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>ER Admissions</td>
                                            <td>
                                                <span className={`label label-${record.ER_Admission_Count > 0 ? "danger" : "success"}`}>
                                                    {record.ER_Admission_Count ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* IP */}
                        <div className="col-md-6">
                            <div style={panelStyle}>
                                <div style={sectionTitleStyle}>
                                    <span>🏨</span> Inpatient Activity
                                </div>
                                <table className="table table-condensed" style={{ marginBottom: 0 }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>Last IP Visit</td>
                                            <td style={{ fontWeight: "600" }}>{formatDate(record.Date_of_most_recent_IP_visit)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>IP Visits (past 6 days)</td>
                                            <td>
                                                <span className={`label label-${record.Number_of_IP_visits_in_the_past_6_days > 0 ? "danger" : "default"}`}>
                                                    {record.Number_of_IP_visits_in_the_past_6_days ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>IP Visits (past 29 days)</td>
                                            <td>
                                                <span className={`label label-${record.Number_of_IP_visits_in_the_past_29_days > 0 ? "warning" : "default"}`}>
                                                    {record.Number_of_IP_visits_in_the_past_29_days ?? 0}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ color: "#777", fontSize: "13px" }}>Data Since</td>
                                            <td style={{ fontWeight: "600" }}>{formatDate(record.Data_History_Date)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── CONDITION BREAKDOWN BAR ── */}
                    <div style={panelStyle}>
                        <div className="row" style={{ marginBottom: "10px" }}>
                            <div className="col-xs-8">
                                <strong style={{ fontSize: "14px", color: "#2c3e50" }}>
                                    Condition Coverage — {activeConditions.length} of {totalConditions} detected
                                </strong>
                            </div>
                            <div className="col-xs-4 text-right">
                                <span style={{ fontSize: "12px", color: "#aaa" }}>
                                    {Math.round((activeConditions.length / totalConditions) * 100)}% positive
                                </span>
                            </div>
                        </div>
                        <div style={{ height: "10px", backgroundColor: "#eef0f5", borderRadius: "8px", overflow: "hidden" }}>
                            <div style={{
                                height: "100%",
                                width: `${(activeConditions.length / totalConditions) * 100}%`,
                                background: "linear-gradient(90deg, #e74c3c, #c0392b)",
                                borderRadius: "8px", transition: "width 0.6s ease",
                                minWidth: activeConditions.length > 0 ? "4px" : "0",
                            }} />
                        </div>
                        <div className="row" style={{ marginTop: "10px" }}>
                            <div className="col-xs-6">
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#e74c3c", fontWeight: "600" }}>
                                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#e74c3c", display: "inline-block" }} />
                                    {activeConditions.length} Detected
                                </span>
                            </div>
                            <div className="col-xs-6 text-right">
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#27ae60", fontWeight: "600" }}>
                                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#27ae60", display: "inline-block" }} />
                                    {totalConditions - activeConditions.length} Not Detected
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── DISEASE CARDS ── */}
                    <div style={panelStyle}>
                        <div style={{ ...sectionTitleStyle, justifyContent: "space-between", flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span>🩺</span> Disease Conditions Overview
                            </span>
                            <div style={{ marginTop: "4px" }}>
                                {[
                                    { key: "all", label: "All" },
                                    { key: "active", label: "Detected" },
                                    { key: "inactive", label: "Not Detected" },
                                ].map((f) => (
                                    <button
                                        key={f.key}
                                        onClick={() => this.setState({ activeFilter: f.key })}
                                        style={{
                                            ...filterBtnBase,
                                            backgroundColor: activeFilter === f.key ? "#1a1a2e" : "#fff",
                                            color: activeFilter === f.key ? "#fff" : "#555",
                                            borderColor: activeFilter === f.key ? "#1a1a2e" : "#ddd",
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={diseaseGridStyle}>{filteredDiseases}</div>

                        {filteredDiseases.length === 0 && (
                            <div className="text-center" style={{ padding: "40px", color: "#aaa" }}>
                                <div style={{ fontSize: "40px", marginBottom: "10px" }}>🎉</div>
                                <p style={{ fontSize: "15px" }}>No conditions in this category.</p>
                            </div>
                        )}
                    </div>

                    {/* ── FOOTER ── */}
                    <div className="text-center" style={{ marginTop: "20px", color: "#bbb", fontSize: "11px" }}>
                        Data Source: {record.Participant || "—"}&nbsp;|&nbsp;
                        Generated: {formatDate(record.Report_Date_Time)}
                    </div>

                </div>
            </div>
        );
    }
}

export default ChronicConditions;
